import { supabase } from '@/integrations/supabase/client';

// `rewriteSupabaseStorageHost` moved to `./supabaseStorageUrl` (ENT-2806) so the
// document sanitizer can reuse it without importing the Supabase client. It is
// still exported from here for existing callers/tests that import it from
// `@/lib/logoUrl`.
import { rewriteSupabaseStorageHost } from './supabaseStorageUrl';

export { rewriteSupabaseStorageHost } from './supabaseStorageUrl';

export function sanitizeLogoUrl(url?: string | null): string | null {
  if (!url) return null;

  const trimmed = url.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('data:image/')) return trimmed;
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return trimmed;

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    // Transitional: rewrite stale storage hosts on the read path until
    // every caller passes `primaryPath` / `primaryBucket`. Drops in Phase 3.
    return rewriteSupabaseStorageHost(parsed.toString());
  } catch {
    return null;
  }
}

// Resolve a storage path to an env-correct public URL using the runtime
// Supabase client. `getPublicUrl()` is pure string concat under the hood, so
// this is safe to call in render paths.
//
// Use this whenever the database holds a storage *path* (e.g.
// `marketplace_listings.logo_path`) rather than an absolute URL — that's the
// canonical shape per ENT-1115. The path-only column eliminates the
// cross-environment-drift hazard that ENT-1112 / ENT-1116 surfaced (absolute
// URLs baked at write time travel verbatim across DB exports).
export function resolveLogoStorageUrl(
  bucket: string,
  path?: string | null,
): string | null {
  if (!bucket || !path) return null;
  const trimmed = path.trim();
  if (!trimmed) return null;
  // Strip any leading slash so callers can pass either `org-id/file.png`
  // or `/org-id/file.png` without producing a `//` artefact in the URL.
  const normalised = trimmed.replace(/^\/+/, '');
  return supabase.storage.from(bucket).getPublicUrl(normalised).data.publicUrl;
}

export function getDisplayLogoUrl(options: {
  primaryUrl?: string | null;
  primaryPath?: string | null;
  primaryBucket?: string | null;
  domain?: string | null;
  websiteUrl?: string | null;
  cacheBustKey?: string | null;
}): string | null {
  // ENT-3183: `logo_url` is the single source of truth. The app only ever
  // writes `logo_url` — an absolute storage URL for uploads (host-normalized on
  // read by sanitizeLogoUrl → rewriteSupabaseStorageHost), or an external URL.
  // `logo_path` was a canonical-shape column that nothing ever wrote; giving it
  // read precedence let a one-time backfilled value shadow every fresh upload
  // (re-upload-invisible / broken-image-from-dead-path). We now prefer
  // `logo_url` and keep `logo_path` only as a fallback for the (currently zero)
  // rows that have a path but no URL. The column is removed in the follow-up.
  const fromPath = options.primaryBucket
    ? resolveLogoStorageUrl(options.primaryBucket, options.primaryPath)
    : null;
  const resolvedUrl = sanitizeLogoUrl(options.primaryUrl) || fromPath;

  if (!resolvedUrl) {
    return null;
  }

  if (resolvedUrl.startsWith('data:') || !options.cacheBustKey) {
    return resolvedUrl;
  }

  return `${resolvedUrl}${resolvedUrl.includes('?') ? '&' : '?'}v=${encodeURIComponent(options.cacheBustKey)}`;
}
